import {
  all, call, delay, put, select, takeLatest,
} from 'redux-saga/effects'
import { ReturnCall } from '../types'
import { actions as appActions } from '../state'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  GenerateListsPayload,
  GetAvailableRestockListsPayload,
  GetProductsListByCSVSagaParams,
  ItemRestocking,
  FinishRestockingAddressPayload,
  ItemsListPayload,
  ProductsListPayload,
  RemoveRestockingItemPayload,
  RestockingBatch,
  RestockingLists,
  SearchRestockListsPayload,
  StartRestockingAddressPayload,
  StartRestockingListPagePayload,
  CollectRestockingAddress,
  UpdateRestockingListPayload,
  User,
  ValidateBadgePayload,
  RestockingFullcartPayload,
  OperatorUnlinkPayload,
  StartOrFinishRestockingAddressRestockPayload,
  ItemToRestock,
  FinishItemRestockUnfit,
  ValidateProductPayload,
  OperationDataTypeEnum,
  FinishedPage,
  ItemsToCollect,
  ValidateRestockAddressPayload,
  ReportItemQuantityPerBoxPayload,
  CheckInValidatePayload,
  CheckinGenerateRestockListPayload,
  ValidateCollectAddressPayload,
  FinishCollectAddressEmptyGondolaPayload,
  RestockingValidateEntryCollectAddressPayload,
  GetMovingAddressPayload,
  PrintAddressesLabelPayload,
} from './RestockingTypes'
import { toast } from 'react-toastify'
import { actions, initialState } from './RestockingState'
import { capitalizeString } from '../../utils/capitalize'
import { handleConnectionError } from 'src/utils/checkNetworkStatus'
import apis from './RestockingApi'
import { transferTypes } from './utils'

function* getProductsList(
  data: PayloadAction<ProductsListPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getProductsList> = yield call(
      apis.getProductsList,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar a lista de produtos para reposição!')
      yield put(actions.setProductsList({ productsList: initialState.productsList }))
      return
    }

    yield put(actions.setProductsList({ productsList: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar a lista de produtos para reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getProductsListByCSV(
  { payload }: PayloadAction<GetProductsListByCSVSagaParams>,
) {
  yield put(appActions.isLoading(true))

  const {
    csvFile, idType, setCsvFileAlreadySended, setCsvFileErrors,
  } = payload

  try {
    const result: ReturnCall<typeof apis.getProductsListByCSV> = yield call(
      apis.getProductsListByCSV,
      { csvFile, idType },
    )

    if (!result.isOk) {
      if (result?.data?.data?.rowsWithErrors?.length > 0) {
        const errors = result.data.data.rowsWithErrors.reduce((error: string, rowWithError: number) => `${error}, ${rowWithError}`, '')
        setCsvFileErrors(errors.substring(2, errors.length))
      }
      toast.error(result?.data?.data?.message || 'Falha ao buscar a lista de produtos para reposição!')
      yield put(actions.setProductsList({ productsList: initialState.productsList }))
      return
    }

    yield put(actions.setProductsList({ productsList: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar a lista de produtos para reposição!')
  } finally {
    setCsvFileAlreadySended(true)
    yield put(appActions.isLoading(false))
  }
}

function* generateRestockingLists(data: PayloadAction<GenerateListsPayload>) {
  yield put(appActions.isLoading(true))
  const { idType, history } = data.payload
  try {
    const result: ReturnCall<typeof apis.generateRestockingLists> = yield call(
      apis.generateRestockingLists,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao gerar as listas de reposição!')
      return
    }

    toast.success('Listas geradas com sucesso!')
    yield put(actions.setProductsList({ productsList: initialState.productsList }))

    if (transferTypes.includes(idType)) {
      history.push('/transfers/manage')
    } else {
      history.push('/restocking/manage')
    }
  } catch (err: any) {
    toast.error('Falha ao gerar as listas de reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getRestockingListPages(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    yield put(actions.setRestockingListPages({ restockingListPages: initialState.restockingListPages }))

    const result: ReturnCall<typeof apis.getRestockingListPages> = yield call(
      apis.getRestockingListPages,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar as páginas de produtos!')
      return
    }
    yield put(actions.setRestockingListPages({ restockingListPages: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar as páginas de produtos!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* searchRestockingLists(data: PayloadAction<SearchRestockListsPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.searchRestockingLists> = yield call(
      apis.searchRestockingLists,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar as listas de reposição!')
      return
    }
    yield put(actions.setSelectedFiltersHistory({ selectedFiltersHistory: data.payload }))
    yield put(actions.setRestockingLists({ restockingLists: result.data }))
    yield put(actions.setAutoFetchRestockingLists(false))
  } catch (err: any) {
    toast.error('Falha ao buscar as listas de reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* removeRestockingItem(data: PayloadAction<RemoveRestockingItemPayload>) {
  yield put(appActions.isLoading(true))
  const {
    idToDelete, commonId: idList, itemName, stateName, history,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.removeRestockingItem> = yield call(
      apis.removeRestockingItem,
      data.payload,
    )

    if (!result.isOk) {
      const errorStatusNotFound = 404
      const errorCodeListNotFound = 'LIST_NOT_FOUND'
      const errorCodePageNotFound = 'PAGE_NOT_FOUND'
      const pathPageRestockListManagement = '/restocking/manage'
      const currentPathPage = window.location.pathname

      if (result.data.status === errorStatusNotFound && result.data.data.errorCode === errorCodePageNotFound) {
        toast.error('Página não encontrada! Atualizando lista de páginas...')
        yield put(actions.getRestockingListPages(idList))
        return
      }

      if (result.data.status === errorStatusNotFound && result.data.data.errorCode === errorCodeListNotFound) {
        if (currentPathPage === pathPageRestockListManagement) {
          toast.error('Lista não encontrada! Atualizando listas...')
          yield put(actions.setAutoFetchRestockingLists(true))
          return
        }

        yield put(actions.setRestockingListPages({ restockingListPages: initialState.restockingListPages }))
        toast.error(
          'Lista não encontrada! redirecionando para a página de listas de reposição...',
          { autoClose: 3000 },
        )

        yield delay(3000)
        history.push('/restocking/manage')
        return
      }

      toast.error(result?.data?.data?.message || `Falha ao remover ${itemName}!`)
      return
    }

    const { warning, list } = result.data

    if (warning) {
      toast.info(warning, { autoClose: 3000 })
      const restockingLists: RestockingLists[] = yield select(({ restocking }) => restocking.restockingLists)
      const updatedRestockingLists = restockingLists.map((item) => (item.id === list.id ? list : item))
      yield put(actions.setRestockingLists({ restockingLists: updatedRestockingLists }))
      return
    }

    yield put(actions.removeRestockingItemFromState({ id: idToDelete, stateName }))
    toast.success(`${capitalizeString(itemName)} removida com sucesso!`)
  } catch (err: any) {
    toast.error(`Falha ao remover ${itemName}!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getItemsList(data: PayloadAction<ItemsListPayload>) {
  yield put(appActions.isLoading(true))

  try {
    yield put(actions.setRestockingItemsList({ restockingItemsList: initialState.restockingItemsList }))
    const result: ReturnCall<typeof apis.getItemsList> = yield call(
      apis.getItemsList,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar os itens da lista de reposição!')
      return
    }

    yield put(actions.setRestockingItemsList({ restockingItemsList: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar os itens da lista de reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function groupBatches(batches: RestockingBatch[]): RestockingBatch[] {
  return batches.reduce((acc: RestockingBatch[], batch: RestockingBatch) => {
    const batchExpirationDate = new Date(batch.expirationDate).getTime()
    const existingBatch = acc.find((b) => new Date(b.expirationDate).getTime() === batchExpirationDate)

    if (existingBatch) {
      existingBatch.quantity += batch.quantity
    } else {
      acc.push({ ...batch })
    }

    return acc
  }, [])
}

function mapItems(item: ItemRestocking): ItemRestocking {
  const groupedBatches = groupBatches(item.batches)
  const batches = groupedBatches.map((batch) => ({ ...batch, done: false }))
  return { ...item, done: false, batches }
}

function mapAddresses(address: CollectRestockingAddress): CollectRestockingAddress {
  const items = address.items.map(mapItems)
  return { ...address, done: false, items }
}

export function* validateBadge({ payload }: PayloadAction<ValidateBadgePayload>) {
  const {
    badge, history, onSuccess, setError,
  } = payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const response: ReturnCall<typeof apis.validateBadge> = yield call(
      apis.validateBadge,
      payload,
    )

    if (!response.isOk) {
      setError(response?.data?.data?.message || 'Crachá inválido!')
      return
    }

    const {
      user, type, item, idRestockingType, restockingConfig,
    } = response.data

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history?.push('/restocking/webapp/restock-finished')
      return
    }

    const currentUser: User = {
      user,
      badge,
      startedPage: {
        itemsToCollect: null,
        pausedPage: false,
      },
      itemsToRestock: [],
      finishedPage: null,
    }

    if (idRestockingType) yield put(actions.setProperty({ property: 'idRestockingType', value: idRestockingType }))

    if (restockingConfig) yield put(actions.setProperty({ property: 'restockingConfig', value: restockingConfig }))

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = {
        itemsToCollect: item as ItemsToCollect,
        pausedPage: false,
      }
      const { itemsToCollect } = startedPage
      const restockingAddressMapped = itemsToCollect.addresses.map(mapAddresses)
      const startedPageMapped = { ...startedPage, itemsToCollect: { ...itemsToCollect, addresses: restockingAddressMapped } }
      currentUser.startedPage = startedPageMapped
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      const itemsToRestock = item as ItemToRestock[]
      currentUser.itemsToRestock = itemsToRestock.map((itemToRestock) => ({ ...itemToRestock, done: false }))
    }

    yield put(actions.setProperty({ property: 'user', value: currentUser }))

    onSuccess({
      startedPage: currentUser.startedPage,
      itemsToRestock: currentUser.itemsToRestock,
    })
  } catch (error: any) {
    setError('Falha ao validar o crachá!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateBadgeForListCreation({ payload }: PayloadAction<ValidateBadgePayload>) {
  const {
    badge, onSuccess, setError,
  } = payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const response: ReturnCall<typeof apis.validateBadge> = yield call(
      apis.validateBadge,
      payload,
    )

    if (!response.isOk) {
      setError(response?.data?.data?.message || 'Crachá inválido!')
      return
    }

    const { user } = response.data
    const currentUser: User = yield select(({ restocking }) => restocking.user)
    yield put(actions.setProperty({ property: 'user', value: { ...currentUser, user, badge } }))
    onSuccess()
  } catch (error: any) {
    setError('Falha ao validar o crachá!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* startRestockingList(data: PayloadAction<UpdateRestockingListPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.startRestockingList> = yield call(
      apis.startRestockingList,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao inicializar lista!')
      return
    }

    toast.success('Lista inicializada sucesso!')
    yield delay(3000)
    window.location.reload()
  } catch (err: any) {
    toast.error('Falha ao inicializar lista!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* finishRestockingList(data: PayloadAction<UpdateRestockingListPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.finishRestockingList> = yield call(
      apis.finishRestockingList,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao finalizar lista!')
      return
    }

    toast.success('Lista finalizada sucesso!')
    yield delay(3000)
    window.location.reload()
  } catch (err: any) {
    toast.error('Falha ao finalizar lista!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getFiltersSearchLists(data: PayloadAction<boolean>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getFiltersSearchLists> = yield call(
      apis.getFiltersSearchLists,
      data.payload || false,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter os filtros!')
      return
    }

    yield put(actions.setFilterSearchLocations({ filtersSearchLists: result.data }))
  } catch (err: any) {
    toast.error('Não foi possível obter os filtros!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getAvailableRestockLists(data: PayloadAction<GetAvailableRestockListsPayload>) {
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.getAvailableRestockLists> = yield call(
      apis.getAvailableRestockLists,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter listas de reposição disponíveis!')
      return
    }

    yield put(actions.setAvailableRestockLists({ availableRestockLists: result.data }))
    data.payload.setLoading(false)
  } catch (err: any) {
    toast.error('Não foi possível obter listas de reposição disponíveis!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* startRestockingListPage(data: PayloadAction<StartRestockingListPagePayload>) {
  yield put(appActions.isLoading(true))
  const { history, onError } = data.payload

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.startRestockingListPage> = yield call(
      apis.startRestockingListPage,
      data.payload,
    )

    if (!result.isOk) {
      onError?.()
      const errorStatusForbidden = 403
      const errorCodeOperatorPageStarted = 'OPERATOR_HAS_STARTED_PAGE'

      toast.error(result?.data?.data?.message || 'Não foi possível inicializar a página de reposição!', {
        autoClose: 3000,
      })

      if (result.data.status === errorStatusForbidden && result.data.data.errorCode === errorCodeOperatorPageStarted) {
        yield delay(3100)
        toast.info('Bipe seu crachá, para continuar de onde parou!')
        history.push('/restocking/webapp/badge')
      }

      return
    }

    const {
      type, item, pausedPage, idRestockingType, restockingConfig,
    } = result.data

    if (pausedPage && !type && !item) {
      yield put(actions.setProperty({ property: 'acceptsPageWithPausedStatus', value: true }))
      return
    }

    yield put(actions.setProperty({ property: 'acceptsPageWithPausedStatus', value: false }))
    yield put(actions.setProperty({ property: 'idRestockingType', value: idRestockingType }))
    yield put(actions.setProperty({ property: 'restockingConfig', value: restockingConfig }))

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = {
        itemsToCollect: item as ItemsToCollect,
        pausedPage,
      }
      const restockingAddressMapped = startedPage.itemsToCollect.addresses.map(mapAddresses)
      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))

      if (startedPage.itemsToCollect.entryCollectAddressRequired) {
        history.push('/restocking/webapp/entry-collect-address')
      } else {
        history.push('/restocking/webapp/collect-address')
      }
      return
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      yield put(actions.setUserItemsToRestock(item as ItemToRestock[]))
      history.push('/restocking/webapp/restock-address')
    }
  } catch (err: any) {
    toast.error('Não foi possível inicializar a página de reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* startRestockingAddress(data: PayloadAction<StartRestockingAddressPayload>) {
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const { onSuccess } = data.payload
    const result: ReturnCall<typeof apis.startRestockingAddress> = yield call(
      apis.startRestockingAddress,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível inicializar a coleta no endereço!')
      return
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Não foi possível inicializar a coleta no endereço!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* finishRestockingAddress(data: PayloadAction<FinishRestockingAddressPayload>) {
  const {
    itemAddress, restockingBatchIsDone, addressOrder, idModel, history, setIsDisabled,
  } = data.payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.finishRestockingAddress> = yield call(
      apis.finishRestockingAddress,
      data.payload,
    )

    if (!result.isOk) {
      if (result?.data?.data?.error_code === 'ADDRESS_ALREADY_FINISHED') {
        return
      }
      toast.error(result?.data?.data?.message || 'Não foi possível finalizar a coleta no endereço!')
      return
    }

    const { type, item } = result.data

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      yield put(actions.setItemsToCollect(null))
      yield put(actions.setUserItemsToRestock(item as ItemToRestock[]))
      history.push('/restocking/webapp/restock-address')
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = yield select((state) => state.restocking.user.startedPage)
      const currentCollectRestockingAddress = yield select((state) => state.restocking.currentCollectRestockingAddress)
      const restockingAddressMapped = (item as ItemsToCollect).addresses.map(mapAddresses)

      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))

      const sourceAddress = restockingAddressMapped.find((address) => !address.done)

      if (sourceAddress) {
        yield put(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: sourceAddress,
        }))

        if (currentCollectRestockingAddress.address !== sourceAddress?.address) {
          history.push('/restocking/webapp/collect-address')
        } else if (idModel !== sourceAddress.items[0].idModel) {
          history.push('/restocking/webapp/collect-product')
        }
      }
      return
    }

    if (restockingBatchIsDone) {
      yield put(actions.setRestockingBatchIsDone(restockingBatchIsDone))
    } else if (addressOrder !== undefined && idModel && itemAddress.quantity === -1) {
      yield put(actions.setRestockingItemIsDone({ addressOrder, idModel }))
    }
  } catch (err: any) {
    toast.error('Não foi possível finalizar a coleta no endereço!')
  } finally {
    yield put(appActions.isLoading(false))
    if (setIsDisabled) {
      setIsDisabled(false)
    }
  }
}

function* restockingFullcart(data: PayloadAction<RestockingFullcartPayload>) {
  const { history } = data.payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.restockingFullcart> = yield call(
      apis.restockingFullcart,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível acionar a opção carrinho cheio!')
      return
    }

    const itemsToRestock = result.data

    if (itemsToRestock) {
      yield put(actions.setItemsToCollect(null))
      yield put(actions.setUserItemsToRestock(itemsToRestock))
      yield put(actions.setProperty({
        property: 'currentCollectRestockingAddress',
        value: initialState.currentCollectRestockingAddress,
      }))
      history.push('/restocking/webapp/restock-address')
    }
  } catch (err: any) {
    toast.error('Não foi possível acionar a opção carrinho cheio!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* operatorUnlink(data: PayloadAction<OperatorUnlinkPayload>) {
  const { onSuccess } = data.payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.operatorUnlink> = yield call(
      apis.operatorUnlink,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível desvincular operador da página!')
      return
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Não foi possível desvincular operador da página!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* startRestockingAddressRestock(data: PayloadAction<StartOrFinishRestockingAddressRestockPayload>) {
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const { onSuccess } = data.payload
    const result: ReturnCall<typeof apis.startRestockingAddressRestock> = yield call(
      apis.startRestockingAddressRestock,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível inicializar reposição no endereço!')
      return
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Não foi possível inicializar reposição no endereço!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* finishRestockingAddressRestock(data: PayloadAction<StartOrFinishRestockingAddressRestockPayload>) {
  const {
    history, idModel, itemsToFinish, onSuccess,
  } = data.payload
  yield put(appActions.isLoading(true))
  yield put(actions.setProperty({ property: 'isRestockAddressValid', value: initialState.isRestockAddressValid }))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.finishRestockingAddressRestock> = yield call(
      apis.finishRestockingAddressRestock,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível finalizar reposição no endereço!')
      return
    }

    onSuccess()

    if (idModel) yield put(actions.setUserItemToRestockIsDone(idModel))
    if (itemsToFinish) yield put(actions.setUserItemsToRestockIsDone(itemsToFinish))

    const { type, item } = result.data

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = {
        itemsToCollect: item as ItemsToCollect,
        pausedPage: false,
      }
      const restockingAddressMapped = startedPage.itemsToCollect.addresses.map(mapAddresses)
      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))
      yield put(actions.setUserItemsToRestock(initialState.user.itemsToRestock))

      yield put(actions.setProperty({
        property: 'currentProductToRestock',
        value: initialState.currentProductToRestock,
      }))

      if (startedPage.itemsToCollect.entryCollectAddressRequired) {
        yield put(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: undefined,
        }))

        history.push('/restocking/webapp/entry-collect-address')
      } else {
        const sourceAddress = restockingAddressMapped.find((address) => !address.done)

        yield put(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: sourceAddress,
        }))

        history.push('/restocking/webapp/collect-address')
      }
      return
    }

    const itemsToRestock: ItemToRestock[] = yield select((state) => state.restocking.user.itemsToRestock)

    const itemNotDone = itemsToRestock?.find((itemToRestock) => !itemToRestock.done)

    const lastItemRestocked: ItemToRestock = yield select(({ restocking }) => restocking.currentProductToRestock)

    if (itemNotDone) {
      yield put(actions.setProperty({
        property: 'currentProductToRestock',
        value: itemNotDone,
      }))

      if (lastItemRestocked.location.address !== itemNotDone.location.address) history.push('/restocking/webapp/restock-address')
    }
  } catch (err: any) {
    toast.error('Não foi possível finalizar reposição no endereço!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* finishRestockingAddressRestockUnfit(data: PayloadAction<FinishItemRestockUnfit>) {
  const { history } = data.payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.finishRestockingAddressRestockUnfit> = yield call(
      apis.finishRestockingAddressRestockUnfit,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível finalizar reposição no endereço com opção não coube!')
      return
    }

    const itemsToRestock = result.data

    if (itemsToRestock && itemsToRestock.length > 0) {
      yield put(actions.setUserItemsToRestock(itemsToRestock))
      const itemNotDone = itemsToRestock?.find((item) => !item.done)

      if (itemNotDone) {
        yield put(actions.setProperty({
          property: 'currentProductToRestock',
          value: itemNotDone,
        }))

        history.push('/restocking/webapp/restock-address')
      }
    }

    yield put(actions.setProperty({ property: 'isRestockAddressValid', value: initialState.isRestockAddressValid }))
  } catch (err: any) {
    toast.error('Não foi possível finalizar reposição no endereço com opção não coube!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* restockingValidateProduct(
  data: PayloadAction<ValidateProductPayload>,
) {
  yield put(appActions.isLoading(true))
  const { onSuccess } = data.payload
  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.restockingValidateProduct> = yield call(
      apis.restockingValidateProduct,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível validar o produto para reposição!')
      return
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Não foi possível validar o produto para reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* restockingValidateRestockAddress(
  data: PayloadAction<ValidateRestockAddressPayload>,
) {
  yield put(appActions.isLoading(true))
  yield put(actions.setProperty({ property: 'isRestockAddressValid', value: initialState.isRestockAddressValid }))
  try {
    if (handleConnectionError()) return

    const { onFail } = data.payload
    const result: ReturnCall<typeof apis.restockingValidateRestockAddress> = yield call(
      apis.restockingValidateRestockAddress,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível validar endereço de reposição!')
      onFail()
      return
    }

    yield put(actions.setProperty({ property: 'isRestockAddressValid', value: true }))
  } catch (err: any) {
    toast.error('Não foi possível validar endereço de reposição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* restockingReportQuantityPerBox(
  data: PayloadAction<ReportItemQuantityPerBoxPayload>,
) {
  const { history } = data.payload
  yield put(appActions.isLoading(true))
  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.restockingReportQuantityPerBox> = yield call(
      apis.restockingReportQuantityPerBox,
      data.payload,
    )

    const { type, item } = result.data

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível reportar quantidade por caixa!')
      return
    }

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = yield select((state) => state.restocking.user.startedPage)

      const restockingAddressMapped = (item as ItemsToCollect).addresses.map(mapAddresses)
      const currentCollectRestockingAddress = yield select((state) => state.restocking.currentCollectRestockingAddress)
      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))

      const sourceAddress = restockingAddressMapped.find((address) => !address.done)

      if (sourceAddress) {
        yield put(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: sourceAddress,
        }))

        if (currentCollectRestockingAddress.address !== sourceAddress?.address) {
          history.push('/restocking/webapp/collect-address')
        }
      }
      return
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      yield put(actions.setItemsToCollect(null))
      yield put(actions.setUserItemsToRestock(item as ItemToRestock[]))
      yield put(actions.setProperty({
        property: 'currentCollectRestockingAddress',
        value: initialState.currentCollectRestockingAddress,
      }))
      history.push('/restocking/webapp/restock-address')
    }
  } catch (err: any) {
    toast.error('Não foi possível reportar quantidade por caixa!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getRestockingPageDetails(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))
  try {
    actions.clearProperty('restockingPageDetails')

    const result: ReturnCall<typeof apis.getRestockingPageDetails> = yield call(
      apis.getRestockingPageDetails,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar detalhes da página!')
      return
    }

    yield put(actions.setRestockingPageDetails({ restockingPageDetails: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar detalhes da página!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getRestockingTypes() {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getRestockingTypes> = yield call(
      apis.getRestockingTypes,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao obter tipos de abastecimento')
      return
    }

    yield put(actions.setProperty({ property: 'restockingListTypes', value: result.data }))
  } catch (err: any) {
    toast.error('Falha ao obter tipos de abastecimento!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* restockingCheckinValidate(data: PayloadAction<CheckInValidatePayload>) {
  yield put(appActions.isLoading(true))
  const LIMIT_ORDER_BOX_BARCODES = 10
  try {
    if (handleConnectionError()) return

    const orderCodes: string[] = yield select(({ restocking }) => restocking.orderCodes)

    if (orderCodes.length === LIMIT_ORDER_BOX_BARCODES) {
      toast.error(`Limite de ${LIMIT_ORDER_BOX_BARCODES} pedido(s) atingido!`)
      return
    }

    const result: ReturnCall<typeof apis.restockingCheckinValidate> = yield call(
      apis.restockingCheckinValidate,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível validar o código de barras da caixa do pedido!')
      return
    }

    yield put(actions.setProperty({
      property: 'orderCodes',
      value: [
        ...orderCodes,
        result.data,
      ],
    }))
  } catch (err: any) {
    toast.error('Não foi possível validar o código de barras da caixa do pedido!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* generateCheckinRestockList(data: PayloadAction<CheckinGenerateRestockListPayload>) {
  yield put(appActions.isLoading(true))
  const { onSuccess, onError } = data.payload
  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.generateCheckinRestockList> = yield call(
      apis.generateCheckinRestockList,
      data.payload,
    )

    if (!result.isOk) {
      if (result.data.data.errorCode === 'CHECKIN_ORDERS_HAS_RESTOCKING_LIST' && result.data.data.errors) {
        onError(result.data.data.errors)
      }
      toast.error(result?.data?.data?.message || 'Não foi possível gerar lista de reposição do tipo check-in!')
      return
    }

    const { errors, restockingListId } = result.data

    toast.success('Lista de reposição check-in gerada com sucesso!')
    yield put(actions.setProperty({ property: 'currentIdList', value: restockingListId }))
    onSuccess(errors)
  } catch (err: any) {
    toast.error('Não foi possível gerar lista de reposição do tipo check-in!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateCollectAddress(
  data: PayloadAction<ValidateCollectAddressPayload>,
) {
  yield put(appActions.isLoading(true))
  const { onSuccess, setError } = data.payload
  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.validateCollectAddress> = yield call(
      apis.validateCollectAddress,
      data.payload,
    )

    if (!result.isOk) {
      setError(result?.data?.data?.message || 'Não foi possível reportar quantidade por caixa!')
      return
    }

    onSuccess()
  } catch (err: any) {
    setError('Não foi possível validar o endereço de coleta!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* finishCollectAddressEmptyGondola(data: PayloadAction<FinishCollectAddressEmptyGondolaPayload>) {
  yield put(appActions.isLoading(true))

  const {
    restockingBatchIsDone, idModel, history, setIsDisabled,
  } = data.payload

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.finishCollectAddressEmptyGondola> = yield call(
      apis.finishCollectAddressEmptyGondola,
      data.payload,
    )

    if (!result.isOk) {
      if (result?.data?.data?.error_code === 'ADDRESS_ALREADY_FINISHED') {
        return
      }
      toast.error(result?.data?.data?.message || 'Não foi possível finalizar a coleta no endereço!')
      return
    }

    const { type, item } = result.data

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      yield put(actions.setItemsToCollect(null))
      yield put(actions.setUserItemsToRestock(item as ItemToRestock[]))
      history.push('/restocking/webapp/restock-address')
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = yield select((state) => state.restocking.user.startedPage)
      const currentCollectRestockingAddress = yield select((state) => state.restocking.currentCollectRestockingAddress)
      const restockingAddressMapped = (item as ItemsToCollect).addresses.map(mapAddresses)

      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))

      const sourceAddress = restockingAddressMapped.find((address) => !address.done)

      if (sourceAddress) {
        yield put(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: sourceAddress,
        }))

        if (currentCollectRestockingAddress.address !== sourceAddress?.address) {
          history.push('/restocking/webapp/collect-address')
        } else if (idModel !== sourceAddress.items[0].idModel) {
          history.push('/restocking/webapp/collect-product')
        }
      }
      return
    }

    if (restockingBatchIsDone) {
      yield put(actions.setRestockingBatchIsDone(restockingBatchIsDone))
    }
  } catch (err: any) {
    toast.error('Não foi possível finalizar a coleta no endereço!')
  } finally {
    yield put(appActions.isLoading(false))
    if (setIsDisabled) {
      setIsDisabled(false)
    }
  }
}

function* restockingValidateEntryCollectAddress(data: PayloadAction<RestockingValidateEntryCollectAddressPayload>) {
  yield put(appActions.isLoading(true))

  const { history, setDisabled } = data.payload

  try {
    if (handleConnectionError()) return

    const result: ReturnCall<typeof apis.restockingValidateEntryCollectAddress> = yield call(
      apis.restockingValidateEntryCollectAddress,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao validar a gaiola!')
      return
    }

    const { type, item } = result.data

    if (type === OperationDataTypeEnum.FINISHED) {
      yield put(actions.setUserFinishedPage(item as FinishedPage))
      history.push('/restocking/webapp/restock-finished')
      return
    }

    if (type === OperationDataTypeEnum.COLLECT) {
      const startedPage = yield select((state) => state.restocking.user.startedPage)
      const restockingAddressMapped = (item as ItemsToCollect).addresses.map(mapAddresses)

      const startedPageMapped = { ...startedPage, itemsToCollect: { ...startedPage.itemsToCollect, addresses: restockingAddressMapped } }
      yield put(actions.setUserStartedPage(startedPageMapped))

      history.push('/restocking/webapp/collect-address')
      return
    }

    if (type === OperationDataTypeEnum.RESTOCK) {
      yield put(actions.setUserItemsToRestock(item as ItemToRestock[]))
      history.push('/restocking/webapp/restock-address')
    }
  } catch (err: any) {
    toast.error('Falha ao validar o endereço para coleta!')
  } finally {
    setDisabled(false)
    yield put(appActions.isLoading(false))
  }
}

function* fetchMovingAddressSaga(action: PayloadAction<GetMovingAddressPayload>) {
  yield put(appActions.isLoading(true))

  const {
    addressType,
    fc,
    availability,
  } = action.payload

  try {
    const result: ReturnCall<typeof apis.getMovingAddress> = yield call(
      apis.getMovingAddress,
      addressType,
      fc,
      availability,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar endereços de movimentação')
      return
    }

    yield put(actions.setProperty({ property: 'movingAddresses', value: result.data }))
  } catch (error) {
    toast.error('Falha ao buscar endereços de movimentação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* printAddressesLabel(action: PayloadAction<PrintAddressesLabelPayload>) {
  yield put(appActions.isLoading(true))

  const { addresses, onFinally } = action.payload

  try {
    const result: ReturnCall<typeof apis.printAddressesLabel> = yield call(
      apis.printAddressesLabel,
      addresses,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao imprimir etiqueta!')
      return
    }

    if (addresses.length === 1) {
      toast.success('Etiqueta enviada para impressão!')
    } else if (addresses.length > 1) {
      toast.success('Etiquetas enviadas para impressão!')
    }
  } catch (err) {
    toast.error('Erro inesperado ao imprimir etiqueta!')
  } finally {
    yield put(appActions.isLoading(false))
    onFinally()
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getProductsList.type, getProductsList),
    takeLatest(actions.getProductsListByCSV.type, getProductsListByCSV),
    takeLatest(actions.generateRestockingLists.type, generateRestockingLists),
    takeLatest(actions.getRestockingListPages.type, getRestockingListPages),
    takeLatest(actions.removeRestockingItem.type, removeRestockingItem),
    takeLatest(actions.getItemsList.type, getItemsList),
    takeLatest(actions.validateBadge.type, validateBadge),
    takeLatest(actions.startRestockingList.type, startRestockingList),
    takeLatest(actions.finishRestockingList.type, finishRestockingList),
    takeLatest(actions.searchRestockingLists.type, searchRestockingLists),
    takeLatest(actions.getFiltersSearchLists.type, getFiltersSearchLists),
    takeLatest(actions.getAvailableRestockLists.type, getAvailableRestockLists),
    takeLatest(actions.startRestockingListPage.type, startRestockingListPage),
    takeLatest(actions.startRestockingAddress.type, startRestockingAddress),
    takeLatest(actions.finishRestockingAddress.type, finishRestockingAddress),
    takeLatest(actions.restockingFullcart.type, restockingFullcart),
    takeLatest(actions.operatorUnlink.type, operatorUnlink),
    takeLatest(actions.startRestockingAddressRestock.type, startRestockingAddressRestock),
    takeLatest(actions.finishRestockingAddressRestock.type, finishRestockingAddressRestock),
    takeLatest(actions.finishRestockingAddressRestockUnfit.type, finishRestockingAddressRestockUnfit),
    takeLatest(actions.restockingValidateProduct.type, restockingValidateProduct),
    takeLatest(actions.restockingValidateRestockAddress.type, restockingValidateRestockAddress),
    takeLatest(actions.restockingReportQuantityPerBox.type, restockingReportQuantityPerBox),
    takeLatest(actions.getRestockingPageDetails.type, getRestockingPageDetails),
    takeLatest(actions.getRestockingTypes.type, getRestockingTypes),
    takeLatest(actions.validateBadgeForListCreation.type, validateBadgeForListCreation),
    takeLatest(actions.restockingCheckinValidate.type, restockingCheckinValidate),
    takeLatest(actions.generateCheckinRestockList.type, generateCheckinRestockList),
    takeLatest(actions.validateCollectAddress.type, validateCollectAddress),
    takeLatest(actions.finishCollectAddressEmptyGondola.type, finishCollectAddressEmptyGondola),
    takeLatest(actions.restockingValidateEntryCollectAddress.type, restockingValidateEntryCollectAddress),
    takeLatest(actions.fetchMovingAddress.type, fetchMovingAddressSaga),
    takeLatest(actions.printAddressesLabel.type, printAddressesLabel),
  ])
}
